import React, { useContext, useEffect, useState } from "react";
import { ConfirmDialog, Dropdown, Text, DraggableCheckboxList, CheckboxListItem, depascalize } from "sprint-common-web";

import { OnPremOrderListingContext } from "../context/OnPremOrderListingContext";
import {
    ColumnConfig,
    OnPremOrderColumn,
    OnPremOrderListingConfig,
    getSortedOrderColumns
} from "./OnPremOrderListingConfig";

interface SetOnPremOrderListingColumnsConfigDialogProps {
    isOpen: boolean;
    pageSize: number;
    setPageSize(pageSize: number): void;
    onClose(): void;
}

export const SetOnPremOrderListingColumnsConfigDialog: React.VFC<SetOnPremOrderListingColumnsConfigDialogProps> = (
    props: SetOnPremOrderListingColumnsConfigDialogProps
) => {
    const [items, setItems] = useState<CheckboxListItem[]>([]);
    const [pageSize, setPageSize] = useState<number>(props.pageSize);

    const { listingConfig, saveListingConfig } = useContext(OnPremOrderListingContext);

    useEffect(() => {
        const checkboxListItems = getCheckboxListItems(listingConfig);

        setItems(checkboxListItems);
    }, []);

    const handleConfirm = () => {
        props.setPageSize(pageSize);

        const config = mapToListingConfig(items, pageSize);

        saveListingConfig(config);

        props.onClose();
    };

    const handlePageSizeChange = (key: string) => {
        setPageSize(Number(key));
    };

    return (
        <ConfirmDialog
            title={"Listing configuration"}
            text={""}
            confirm={{
                onClick: handleConfirm
            }}
            cancel={{
                onClick: props.onClose
            }}
            width={700}
            contentHeight={400}
        >
            <div style={{ display: "flex" }}>
                <div style={{ width: "50%", float: "left", left: 0, paddingRight: 20 }}>
                    <Text value="Page size" styles={{ root: { paddingBottom: 10 } }} />
                    <Dropdown
                        options={pageSizeDropDownOptions.map((key) => ({ key, text: key }))}
                        selectedKey={String(pageSize)}
                        onChange={handlePageSizeChange}
                    />
                </div>
                <div style={{ width: "50%", float: "right" }}>
                    <Text value="Columns" styles={{ root: { paddingBottom: 10 } }} />
                    <DraggableCheckboxList items={items} setItems={setItems} />
                </div>
            </div>
        </ConfirmDialog>
    );
};

const pageSizeDropDownOptions: string[] = ["10", "20", "50", "100"];

const isItemDisabled = (key: OnPremOrderColumn) => {
    return key === OnPremOrderColumn.PoNumber || key === OnPremOrderColumn.Actions;
};

const getCheckboxListItems = (config: OnPremOrderListingConfig): CheckboxListItem[] => {
    return getSortedOrderColumns(config.columns).map(([key, column]) => {
        const resolvedKey = Number(key);

        return {
            key: resolvedKey,
            text: depascalize(OnPremOrderColumn[resolvedKey]),
            checked: column.visible,
            disabled: isItemDisabled(resolvedKey)
        };
    });
};

const mapToListingConfig = (values: CheckboxListItem[], pageSize: number): OnPremOrderListingConfig => {
    const columns = {} as Record<OnPremOrderColumn, ColumnConfig>;

    let order = 0;

    for (const item of values) {
        const index = Number(item.key) as OnPremOrderColumn;
        columns[index] = { order, visible: item.checked };

        order++;
    }

    return {
        pageSize,
        columns
    };
};
